import { useMemo, useState } from "react";
import { useSubstitutions } from "@/hooks/useSubstitutions";
import { useUsers } from "@/hooks/useUsers";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  PersonPlusIcon,
  SortAscIcon,
  SortDescIcon,
  SortIcon,
} from "@/lib/icons";
import { userName } from "@/lib/responsibleUser";
import type { Substitution } from "@/types";

type SortKey = "substituted" | "substitute" | "start" | "end";

function formatDate(value: string): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString("fi");
}

export function Tuuraajat() {
  const {
    substitutions,
    loading,
    createSubstitution,
    updateSubstitution,
    deleteSubstitution,
  } = useSubstitutions();
  const { users } = useUsers();
  const { dbUser } = useAuth();

  // Substitution management requires the MANAGE_SUBSTITUTIONS capability,
  // whose floor is INTERNAL — so INTERNAL, INTERNAL_PLUS and ADMIN all qualify.
  // INTERNAL_PLUS outranks INTERNAL and must never be excluded here.
  const canManage =
    dbUser?.accessLevel === "ADMIN" ||
    dbUser?.accessLevel === "INTERNAL_PLUS" ||
    dbUser?.accessLevel === "INTERNAL";

  const [sortKey, setSortKey] = useState<SortKey>("start");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Substitution | null>(null);
  const [substitutedUserId, setSubstitutedUserId] = useState<string | null>(null);
  const [substituteUserId, setSubstituteUserId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Substitution | null>(null);

  const userOptions: ComboboxOption[] = useMemo(
    () =>
      users
        .filter((u) => u.isActive)
        .map((u) => ({
          value: u.id,
          label: userName(u),
          description: u.email,
        })),
    [users],
  );

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, userName(u));
    return map;
  }, [users]);

  const displayName = (sub: Substitution, which: "substituted" | "substitute") => {
    const nested =
      which === "substituted" ? sub.substitutedUser : sub.substituteUser;
    if (nested) return userName(nested);
    const id = which === "substituted" ? sub.substitutedUserId : sub.substituteUserId;
    return nameById.get(id) ?? id;
  };

  const sorted = useMemo(() => {
    const rows = [...substitutions];
    const factor = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av = "";
      let bv = "";
      switch (sortKey) {
        case "substituted":
          av = displayName(a, "substituted");
          bv = displayName(b, "substituted");
          break;
        case "substitute":
          av = displayName(a, "substitute");
          bv = displayName(b, "substitute");
          break;
        case "start":
          av = a.startDate;
          bv = b.startDate;
          break;
        case "end":
          av = a.endDate;
          bv = b.endDate;
          break;
      }
      return av.localeCompare(bv) * factor;
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [substitutions, sortKey, sortDir, nameById]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortableHead = ({ label, sortableKey }: { label: string; sortableKey: SortKey }) => (
    <TableHead>
      <button
        type="button"
        className="flex items-center gap-1 hover:text-foreground"
        onClick={() => toggleSort(sortableKey)}
      >
        {label}
        {sortKey === sortableKey ? (
          sortDir === "asc" ? (
            <SortAscIcon className="h-3.5 w-3.5" />
          ) : (
            <SortDescIcon className="h-3.5 w-3.5" />
          )
        ) : (
          <SortIcon className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </TableHead>
  );

  const resetForm = () => {
    setSubstitutedUserId(null);
    setSubstituteUserId(null);
    setStartDate("");
    setEndDate("");
    setFormError(null);
  };

  const openCreate = () => {
    setEditTarget(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (sub: Substitution) => {
    setEditTarget(sub);
    setSubstitutedUserId(sub.substitutedUserId);
    setSubstituteUserId(sub.substituteUserId);
    setStartDate(sub.startDate.slice(0, 10));
    setEndDate(sub.endDate.slice(0, 10));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    if (!substitutedUserId || !substituteUserId || !startDate || !endDate) {
      setFormError("Täytä kaikki kentät");
      return;
    }
    setSubmitting(true);
    try {
      const result = editTarget
        ? await updateSubstitution({
            id: editTarget.id,
            version: editTarget.version,
            substitutedUserId,
            substituteUserId,
            startDate,
            endDate,
          })
        : await createSubstitution({
            substitutedUserId,
            substituteUserId,
            startDate,
            endDate,
          });
      if (result?.success) {
        setDialogOpen(false);
        setEditTarget(null);
        resetForm();
      } else {
        setFormError(
          result?.message ??
            (editTarget
              ? "Tuurauksen päivitys epäonnistui"
              : "Tuurauksen luonti epäonnistui"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteSubstitution(deleteTarget.id, deleteTarget.version);
    setDeleteTarget(null);
  };

  if (loading && substitutions.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-40" />
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {["Tuurattava", "Tuuraaja", "Alkaa", "Päättyy", ""].map((h) => (
                  <TableHead key={h}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4, 5].map((j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6 -mt-6">
        <h1 className="text-lg font-semibold mr-2 flex items-center gap-2">
          <PersonPlusIcon className="h-5 w-5" />
          Tuuraajat
        </h1>
        <div className="flex-1" />
        {canManage && (
          <Button onClick={openCreate} size="sm">
            <AddIcon className="h-4 w-4 mr-1" />
            Lisää tuuraus
          </Button>
        )}
      </div>

      <div className="pt-3">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead label="Tuurattava" sortableKey="substituted" />
                <SortableHead label="Tuuraaja" sortableKey="substitute" />
                <SortableHead label="Alkaa" sortableKey="start" />
                <SortableHead label="Päättyy" sortableKey="end" />
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium">
                    {displayName(sub, "substituted")}
                  </TableCell>
                  <TableCell>{displayName(sub, "substitute")}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(sub.startDate)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(sub.endDate)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(sub)}
                        >
                          <EditIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => setDeleteTarget(sub)}
                        >
                          <DeleteIcon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    Ei tuurauksia
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editTarget ? "Muokkaa tuurausta" : "Lisää tuuraus"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label>Tuurattava (poissa)</Label>
              <Combobox
                options={userOptions}
                value={substitutedUserId}
                onChange={setSubstitutedUserId}
                placeholder="Valitse käyttäjä..."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tuuraaja</Label>
              <Combobox
                options={userOptions}
                value={substituteUserId}
                onChange={setSubstituteUserId}
                placeholder="Valitse tuuraaja..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="start-date">Alkaa</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="end-date">Päättyy</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {formError && (
              <p className="text-sm text-destructive">{formError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setEditTarget(null);
              }}
              disabled={submitting}
            >
              Peruuta
            </Button>
            <Button onClick={handleSave} disabled={submitting}>
              {submitting ? "Tallennetaan..." : "Tallenna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista tuuraus?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  Poistetaanko tuuraus{" "}
                  <strong>{displayName(deleteTarget, "substituted")}</strong> →{" "}
                  <strong>{displayName(deleteTarget, "substitute")}</strong>?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Poista</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
